import React, { useState } from "react";
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Heading,
  Flex,
  Divider,
  TextArea,
  View,
  Text,
  Picker,
  Item,
} from "@adobe/react-spectrum";
import Close from "@spectrum-icons/workflow/Close";
import CustomDatePicker from "./CustomDatePicker/CustomDatePicker";
import CallbackSuccessCard from "./CallbackSuccessCard";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { rescheduleCallbackApi } from "../../api/session-api";
import { toast } from "react-toastify";
import Emitter from "../../emitter";
import { API_CALLBACK_DASHBOARD, TELEMETRY_ERROR } from "../../constants/events";


// Extend dayjs with plugins
dayjs.extend(timezone);
dayjs.extend(utc);

interface RescheduleCallbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callbackData?: any;
  onReschedule?: () => void;
  timeslots: TimeslotOption[];
  userEmail?: string;
}

interface TimeslotOption {
  key: string;
  label: string;
  value: string;
}

const RescheduleCallbackDialog: React.FC<RescheduleCallbackDialogProps> = ({
  isOpen,
  onClose,
  callbackData,
  onReschedule,
  timeslots,
  userEmail,
}) => {
  const timezoneWithAMPM = callbackData?.callbackAttributes?.TIMEZONE;
  const timezone = timezoneWithAMPM?.split(" ")[0];
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>("");
  // const [rescheduleReason, setRescheduleReason] = useState<string>("");
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);


  const handleCancel = () => {
    // Reset form data
    setSelectedDate(null);
    setSelectedTimeslot("");
    // setRescheduleReason("");
    setShowSuccessCard(false);
    setSuccessData(null);
    setIsLoading(false);
    onClose();
  };

  // Handle success card close
  const handleSuccessCardClose = () => {
    setShowSuccessCard(false);
    setSuccessData(null);
    handleCancel(); 
  };

  // Check if all required fields are filled
  const isFormValid = selectedDate && selectedTimeslot 
  // && rescheduleReason?.trim();

  const handleReschedule = async () => {
    if (!isFormValid) {
      return;
    }

    setIsLoading(true);
    
    // Extract start time from timeslot (e.g., "08:00:00  -  08:30:00" -> "08:00:00")
    const startTime = selectedTimeslot.split("  -  ")[0];
    
    // Parse the time components (HH:mm:ss format)
    const [hours, minutes] = startTime.split(':').map(Number);
    
    // Create a proper datetime string in customer timezone
    const dateTimeString = selectedDate?.format("YYYY-MM-DD");
    const customerDateTime = dayjs.tz(dateTimeString, timezone)
      .hour(hours)
      .minute(minutes)
    
    // Create epoch time
    const epochTime = customerDateTime.unix();

    try {
      const rescheduleData = {
        contactId: callbackData?.contactId,
        callbackTime: epochTime,
        phoneNumber: callbackData?.phoneNumber,
      };

      const response = await rescheduleCallbackApi(rescheduleData);
      
      if (response && response !== null && response.status === "SUCCESS") {
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALLBACK_DASHBOARD,
            message: "Callback Rescheduled successfully by " + userEmail,
            data : rescheduleData
          },
        });
        const successDetails = {
          timezone: timezone,
          date: selectedDate?.format("ddd, MMM DD, YYYY"),
          timeslot: selectedTimeslot,
          // reason: rescheduleReason,
        };

        // console.log("Reschedule successful, showing success card:", successDetails);

        setSuccessData(successDetails);
        setShowSuccessCard(true);

        if (onReschedule) {
          onReschedule();
        }
      } else {
        console.error("Failed to reschedule callback - API returned null or error");
        toast.error("Failed to reschedule callback");
        handleCancel();
      }
    } catch (error) {
      console.error("Error rescheduling callback:", error);
      toast.error("An error occurred while rescheduling. Please try again.");
      handleCancel();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
  };

  const getDisabledKeys = () => {
    const disabledKeys: string[] = [];
    
    // Always disable the currently selected timeslot
    const selectedSlotKey = timeslots.find(slot => slot.value === selectedTimeslot)?.key;
    if (selectedSlotKey) {
      disabledKeys.push(selectedSlotKey);
    }
    
    // If selected date is today, disable past timeslots and next available slot
    if (selectedDate && selectedDate.isSame(dayjs().tz(timezone), 'day')) {
      const currentTime = dayjs().tz(timezone);
      let nextAvailableSlotFound = false;
      let nextAvailableSlotKey: string | null = null;
      
      timeslots.forEach(slot => {
        const slotStartTime = dayjs().tz(timezone).hour(parseInt(slot.key.split(':')[0])).minute(parseInt(slot.key.split(':')[1]));
        
        if (slotStartTime.isBefore(currentTime) || slotStartTime.isSame(currentTime, 'minute')) {
          disabledKeys.push(slot.key);
        } else if (!nextAvailableSlotFound) {
          nextAvailableSlotKey = slot.key;
          nextAvailableSlotFound = true;
          disabledKeys.push(slot.key); // Disable the next available slot
        }
      });
    }
    return disabledKeys;
  };

  return (
    <>
      {/* Success Card */}
      <CallbackSuccessCard
        isOpen={showSuccessCard}
        onClose={handleSuccessCardClose}
        title="Callback is rescheduled"
        subtitle="Your callback has been rescheduled with the below details"
        details={successData}
        type="reschedule"
      />

      {/* Main Dialog */}
      <DialogContainer onDismiss={handleCancel}>
        {isOpen && !showSuccessCard && (
        <Dialog size="M">
          <Heading>
            <Flex alignItems="center" justifyContent="space-between">
              <Text>Reschedule Callback</Text>
              <span
                style={{ 
                  cursor: "pointer", 
                  fontSize: "24px", 
                  color: "#999",
                  padding: "8px",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px"
                }}
                onClick={handleCancel}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999';
                }}
              >
                <Close />
              </span>
            </Flex>
          </Heading>
          <Divider />
          <Content>
            <View padding="size-200">
              <Flex direction="column" gap="size-200">
                {/* Question heading */}
                <Text UNSAFE_style={{ fontSize: "16px", fontWeight: "600", color: "#323232" }}>
                  When would you like to reschedule the callback?
                </Text>

                {/* Timezone display */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Customer Timezone
                  </Text>
                  <View 
                    UNSAFE_style={{ 
                      padding: "8px 12px", 
                      border: "1px solid #d3d3d3", 
                      borderRadius: "4px", 
                      backgroundColor: "#f5f5f5",
                      fontSize: "14px",
                      color: "#323232"
                    }}
                  >
                    <Text>{timezone || "-"}</Text>
                  </View>
                </View>

                {/* Date Picker */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", display: "block" }}>
                    Select date
                  </Text>
                  <CustomDatePicker
                    onDateSelect={handleDateSelect}
                    selectedDate={selectedDate || undefined}
                    width = "100%"
                  />
                </View>

                {/* Timeslot selection */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Select timeslot
                  </Text>
                  <Picker
                    label=""
                    selectedKey={timeslots.find(slot => slot.value === selectedTimeslot)?.key || ""}
                    onSelectionChange={(key) => {
                      const selectedOption = timeslots.find(slot => slot.key === key);
                      if (selectedOption) {
                        setSelectedTimeslot(selectedOption.value);
                      }
                    }}
                    width="100%"
                    disabledKeys={getDisabledKeys()}
                  >
                    {timeslots?.map((timeslot) => (
                      <Item key={timeslot.key} >{timeslot.label}</Item>
                    ))}
                  </Picker>
                </View>

                {/*Hiding Reason for reschedule */}
                {/* <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Reason for reschedule
                  </Text>
                  <TextArea
                    value={rescheduleReason}
                    onChange={setRescheduleReason}
                    width="100%"
                    height="80px"
                    UNSAFE_style={{
                      // border: rescheduleReason.trim() ? "2px solid #4CAF50" : "1px solid #d3d3d3",
                      borderRadius: "4px"
                    }}
                  />
                   {rescheduleReason.trim() && (
                  <Text UNSAFE_style={{ 
                    fontSize: '12px', 
                    color: '#4caf50',
                    marginTop: '4px'
                  }}>
                    ✓ Reason provided
                  </Text>
                )}
                </View> */}

                {/* Action buttons */}
                <Flex justifyContent="end" gap="size-100" marginTop="size-200">
                  <Button
                    variant="secondary"
                    onPress={handleCancel}
                    UNSAFE_style={{
                      backgroundColor: "#f5f5f5",
                      color: "#323232",
                      border: "1px solid #d3d3d3",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="cta"
                    onPress={handleReschedule}
                    isDisabled={!isFormValid || isLoading}
                    UNSAFE_style={{
                      backgroundColor: (isFormValid && !isLoading) ? "#0265dc" : "#d3d3d3",
                      color: "#ffffff",
                      cursor: (isFormValid && !isLoading) ? "pointer" : "not-allowed"
                    }}
                  >
                    {isLoading ? "Rescheduling..." : "Reschedule"}
                  </Button>
                </Flex>
              </Flex>
            </View>
          </Content>
        </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default RescheduleCallbackDialog;
